import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { LcPoolSummary } from '../hooks/useLiquidityCachePools';
import { api } from '@/lib/api';

interface WithdrawPanelProps {
  pool: LcPoolSummary;
  userLpShares: string;
  onWithdrawComplete: () => void;
}

export function WithdrawPanel({ pool, userLpShares, onWithdrawComplete }: WithdrawPanelProps) {
  const [lpShares, setLpShares] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalUserShares = BigInt(userLpShares || '0');
  const hasShares = totalUserShares > 0n;

  const handleWithdraw = async () => {
    if (!lpShares) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.liquidityCacheWithdraw(pool.poolId, { lpShares });
      setLpShares('');
      onWithdrawComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Preview withdraw amounts
  const preview = (() => {
    try {
      const shares = BigInt(lpShares || '0');
      const total = BigInt(pool.totalLpShares);
      if (shares <= 0n || total === 0n) return null;
      const amountA = (shares * BigInt(pool.reserveA)) / total;
      const amountB = (shares * BigInt(pool.reserveB)) / total;
      return { amountA: amountA.toString(), amountB: amountB.toString() };
    } catch {
      return null;
    }
  })();

  const handleSliderChange = (value: number[]) => {
    if (totalUserShares === 0n) return;
    const pct = value[0];
    const shares = (totalUserShares * BigInt(pct)) / 100n;
    setLpShares(shares.toString());
  };

  return (
    <div className="space-y-4">
      {!hasShares ? (
        <p className="text-sm text-muted-foreground">You have no LP shares in this cache.</p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">Your LP shares: {userLpShares}</p>

          <div>
            <Label>LP Shares to Withdraw</Label>
            <Input
              type="text"
              value={lpShares}
              onChange={(e) => setLpShares(e.target.value)}
              placeholder="0"
            />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Quick select</Label>
            <Slider defaultValue={[0]} max={100} step={25} onValueChange={handleSliderChange} />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          {preview && (
            <div className="rounded-md border p-3 text-sm space-y-1">
              <p>
                You receive: {preview.amountA} {pool.tokenA.symbol} + {preview.amountB}{' '}
                {pool.tokenB.symbol}
              </p>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button onClick={handleWithdraw} disabled={!lpShares || submitting} className="w-full">
            {submitting ? 'Withdrawing...' : 'Withdraw'}
          </Button>
        </>
      )}
    </div>
  );
}
